/**
 * Gift With Purchase (GWP) Manager
 *
 * Handles:
 * - Auto-adding GWP product when cart meets minimum spend
 * - Progress bar updates in cart drawer
 * - Session storage to prevent re-adding if user removes GWP
 * - Manual add button when user wants to add back removed GWP
 */

(function() {
  'use strict';

  // Configuration from Liquid
  const GWP_CONFIG = {
    enabled: true,
    minimumSpend: 10000,
    autoAdd: true,
    variantId: null,
    productId: null,
    currencySymbol: '$'
  };

  // Session storage keys
  const STORAGE_KEYS = {
    dismissed: 'gwp_dismissed',
    addedToCart: 'gwp_added'
  };

  // GWP Manager Class
  class GWPManager {
    constructor() {
      if (!GWP_CONFIG.enabled || !GWP_CONFIG.variantId) {
        return;
      }

      this.minimumSpend = GWP_CONFIG.minimumSpend;
      this.variantId = GWP_CONFIG.variantId;
      this.productId = GWP_CONFIG.productId;
      this.autoAdd = GWP_CONFIG.autoAdd;
      this.currencySymbol = GWP_CONFIG.currencySymbol;

      this.init();
    }

    init() {
      // Bind methods
      this.updateProgress = this.updateProgress.bind(this);
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
      this.addGWPToCart = this.addGWPToCart.bind(this);
      this.handleManualAdd = this.handleManualAdd.bind(this);

      // Initial cart fetch
      this.fetchCart().then(cart => {
        this.handleCartUpdate(cart);
      });

      // Listen for cart updates
      this.setupCartListeners();

      // Setup manual add button
      this.setupManualAddButton();
    }

    /**
     * Setup listeners for cart changes
     */
    setupCartListeners() {
      // Listen for Shopify cart events (if using AJAX cart)
      document.addEventListener('cart:updated', (e) => {
        if (e.detail && e.detail.cart) {
          this.handleCartUpdate(e.detail.cart);
        } else {
          this.fetchCart().then(cart => this.handleCartUpdate(cart));
        }
      });

      // Listen for form submissions that add to cart
      document.addEventListener('submit', (e) => {
        if (e.target.matches('form[action*="/cart/add"]')) {
          // Wait for cart to update
          setTimeout(() => {
            this.fetchCart().then(cart => this.handleCartUpdate(cart));
          }, 1000);
        }
      });

      // Listen for SlideCart HQ events if available
      if (window.SlideCart) {
        const originalOpen = window.SlideCart.open;
        window.SlideCart.open = (...args) => {
          originalOpen.apply(window.SlideCart, args);
          setTimeout(() => {
            this.fetchCart().then(cart => this.handleCartUpdate(cart));
          }, 300);
        };
      }

      // Observe for cart drawer opening (generic approach)
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' || mutation.type === 'childList') {
            const cartDrawer = document.querySelector('#slidecarthq, .cart-drawer, [data-cart-drawer]');
            if (cartDrawer && window.getComputedStyle(cartDrawer).display !== 'none') {
              this.fetchCart().then(cart => this.handleCartUpdate(cart));
            }
          }
        });
      });

      // Start observing body for cart drawer changes
      observer.observe(document.body, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['class', 'style']
      });

      // Poll for cart changes (fallback)
      setInterval(() => {
        this.fetchCart().then(cart => this.handleCartUpdate(cart));
      }, 5000);
    }

    /**
     * Setup manual add button click handler
     */
    setupManualAddButton() {
      document.addEventListener('click', (e) => {
        if (e.target.matches('[data-gwp-add-button]') || e.target.closest('[data-gwp-add-button]')) {
          e.preventDefault();
          this.handleManualAdd();
        }
      });
    }

    /**
     * Fetch current cart state
     */
    async fetchCart() {
      try {
        const response = await fetch('/cart.js');
        return await response.json();
      } catch (error) {
        console.error('GWP: Error fetching cart', error);
        return null;
      }
    }

    /**
     * Handle cart update
     */
    handleCartUpdate(cart) {
      if (!cart || !cart.items) return;

      // Calculate cart total excluding GWP product
      const cartTotalWithoutGWP = this.getCartTotalWithoutGWP(cart);
      const criteriaMet = cartTotalWithoutGWP >= this.minimumSpend;
      const gwpInCart = this.isGWPInCart(cart);
      const wasDismissed = this.wasDismissed();

      // Update progress UI
      this.updateProgress(cartTotalWithoutGWP, criteriaMet, gwpInCart, wasDismissed);

      // Auto-add logic
      if (criteriaMet && !gwpInCart && this.autoAdd && !wasDismissed) {
        this.addGWPToCart();
      }

      // If GWP was in cart but now removed by user, mark as dismissed
      if (this.wasAddedToCart() && !gwpInCart && criteriaMet) {
        this.setDismissed(true);
      }
    }

    /**
     * Calculate cart total excluding GWP products
     * GWP items are identified by: _gwp property OR price reduced to $0 by automatic discount
     */
    getCartTotalWithoutGWP(cart) {
      if (!cart.items) return 0;
      return cart.items.reduce((total, item) => {
        if (item.product_id === this.productId || item.variant_id === this.variantId) {
          // Check if it's a GWP (our code or Shopify's automatic discount)
          const isOurGWP = item.properties && item.properties._gwp === 'true';
          const isShopifyGWP = item.final_line_price === 0 && item.discounts && item.discounts.length > 0;
          if (isOurGWP || isShopifyGWP) {
            return total; // Exclude GWP items
          }
        }
        return total + item.final_line_price;
      }, 0);
    }

    /**
     * Check if GWP product is in cart
     * Detects both our GWP (with _gwp property) and Shopify's automatic discount GWP (price $0)
     */
    isGWPInCart(cart) {
      if (!cart.items) return false;
      return cart.items.some(item => {
        if (item.product_id !== this.productId && item.variant_id !== this.variantId) return false;
        
        // Our GWP (has _gwp property)
        if (item.properties && item.properties._gwp === 'true') return true;
        
        // Shopify's automatic discount GWP (price is $0 with discount applied)
        if (item.final_line_price === 0 && item.discounts && item.discounts.length > 0) return true;
        
        return false;
      });
    }

    /**
     * Update progress bar and messaging
     */
    updateProgress(cartTotal, criteriaMet, gwpInCart, wasDismissed) {
      const progressContainer = document.querySelector('#gwp-cart-progress, [data-gwp-enabled]');
      if (!progressContainer) return;

      const progressBar = progressContainer.querySelector('[data-gwp-progress-bar]');
      const initialMessage = progressContainer.querySelector('[data-gwp-initial-message]');
      const successMessage = progressContainer.querySelector('[data-gwp-success-message]');
      const addCTA = progressContainer.querySelector('[data-gwp-add-cta]');

      // Calculate progress percentage
      const progressPercent = Math.min((cartTotal / this.minimumSpend) * 100, 100);

      // Update progress bar
      if (progressBar) {
        progressBar.style.width = `${progressPercent}%`;
      }

      // Update remaining amount in initial message
      if (initialMessage && !criteriaMet) {
        const remaining = (this.minimumSpend - cartTotal) / 100;
        const formattedRemaining = this.currencySymbol + remaining.toFixed(2);
        const messageHTML = initialMessage.innerHTML.replace(/{remaining}/g, formattedRemaining);
        initialMessage.innerHTML = messageHTML;
      }

      // Toggle messages and CTA based on criteria
      if (criteriaMet) {
        progressContainer.classList.add('criteria-met');
        if (initialMessage) initialMessage.classList.add('hidden');
        if (successMessage) successMessage.classList.remove('hidden');

        // Show add button if dismissed and not in cart
        if (addCTA) {
          if (wasDismissed && !gwpInCart) {
            addCTA.classList.remove('hidden');
          } else {
            addCTA.classList.add('hidden');
          }
        }
      } else {
        progressContainer.classList.remove('criteria-met');
        if (initialMessage) initialMessage.classList.remove('hidden');
        if (successMessage) successMessage.classList.add('hidden');
        if (addCTA) addCTA.classList.add('hidden');

        // Reset dismissed status if cart falls below minimum
        this.setDismissed(false);
      }
    }

    /**
     * Add GWP product to cart
     */
    async addGWPToCart() {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [{
              id: this.variantId,
              quantity: 1,
              properties: {
                '_gwp': 'true',
                '_gift': 'Complimentary Gift'
              }
            }]
          })
        });

        if (response.ok) {
          this.setAddedToCart(true);

          // Dispatch cart update event
          document.dispatchEvent(new CustomEvent('cart:updated'));

          // Trigger cart drawer refresh if available
          this.refreshCartDrawer();
        }
      } catch (error) {
        console.error('GWP: Error adding to cart', error);
      }
    }

    /**
     * Handle manual add button click
     */
    async handleManualAdd() {
      // Clear dismissed status
      this.setDismissed(false);

      // Add to cart
      await this.addGWPToCart();

      // Hide the add button
      const addCTA = document.querySelector('[data-gwp-add-cta]');
      if (addCTA) {
        addCTA.classList.add('hidden');
      }
    }

    /**
     * Refresh cart drawer content
     */
    refreshCartDrawer() {
      // SlideCart HQ refresh
      if (window.SlideCart && typeof window.SlideCart.refresh === 'function') {
        window.SlideCart.refresh();
      }

      // AMP Slide Cart refresh
      if (window.slidecart && typeof window.slidecart.reload === 'function') {
        window.slidecart.reload();
      }

      // Generic cart drawer refresh
      const refreshEvent = new CustomEvent('cart:refresh');
      document.dispatchEvent(refreshEvent);
    }

    /**
     * Check if GWP was dismissed this session
     */
    wasDismissed() {
      if (!window.sessionStorage) return false;
      return sessionStorage.getItem(STORAGE_KEYS.dismissed) === 'true';
    }

    /**
     * Set dismissed status
     */
    setDismissed(value) {
      if (!window.sessionStorage) return;
      if (value) {
        sessionStorage.setItem(STORAGE_KEYS.dismissed, 'true');
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.dismissed);
      }
    }

    /**
     * Check if GWP was added to cart this session
     */
    wasAddedToCart() {
      if (!window.sessionStorage) return false;
      return sessionStorage.getItem(STORAGE_KEYS.addedToCart) === 'true';
    }

    /**
     * Set added to cart status
     */
    setAddedToCart(value) {
      if (!window.sessionStorage) return;
      if (value) {
        sessionStorage.setItem(STORAGE_KEYS.addedToCart, 'true');
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.addedToCart);
      }
    }
  }

  // Initialize GWP Manager when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new GWPManager());
  } else {
    new GWPManager();
  }

  // Export for external use
  window.GWPManager = GWPManager;
})();
